import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Landmark, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { api, formatError } from "@/lib/api";
import { Guarantee } from "@/components/Guarantee";
import { PaymentMethods } from "@/components/PaymentMethods";

const INCLUDED = ["3 mesi di percorso (13 settimane)", "1 incontro di gruppo a settimana · 120 min", "1 allenamento online a settimana · 60 min",
  "39 ore di training in totale", "5 guide PDF di materiale didattico", "Programma personalizzato e monitoraggio settimanale", "App dedicata: scheda di allenamento e tracciamento dei progressi", "Follow up nei mesi successivi: il gruppo si riunisce ogni 3 mesi · 120 min"];

const Confirmed = ({ name }) => (
  <div data-testid="enrollment-paid">
    <CheckCircle2 size={40} className="text-[#c4dcca]" />
    <h1 className="mt-6 font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">Iscrizione<br /><span className="text-[#c4dcca]">confermata.</span></h1>
    <p className="mt-6 max-w-xl text-base text-[#a3b1aa]">Grazie {name}! Il tuo posto è confermato: ti contatterò a breve per fissare il primo appuntamento e inviarti il materiale didattico.</p>
    <Link to="/" data-testid="enrollment-home-link" className="mt-10 inline-block font-mono text-xs uppercase tracking-widest text-[#a3b1aa] underline hover:text-[#c4dcca]">Torna al sito</Link>
  </div>
);

const BankDetails = ({ bank }) => (
  <div data-testid="enrollment-bank-details">
    <Landmark size={40} className="text-[#c4dcca]" />
    <h1 className="mt-6 font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">Dati per<br /><span className="text-[#c4dcca]">il bonifico.</span></h1>
    <p className="mt-6 max-w-xl text-base text-[#a3b1aa]">Ti ho inviato questi dati anche via email. Appena ricevo il bonifico ti scrivo per confermare l'iscrizione.</p>
    <dl className="mt-8 grid max-w-2xl gap-px bg-[rgba(152,191,162,0.2)]">
      {[["Intestatario", bank.holder], ["IBAN", bank.iban], ["Importo", `${bank.amount} €`], ["Causale", bank.reason]].map(([k, v]) => (
        <div key={k} className="grid gap-1 bg-[#0b120e] p-5 md:grid-cols-[160px_1fr]">
          <dt className="font-mono text-xs uppercase tracking-widest text-[#98bfa2]">{k}</dt>
          <dd data-testid={`bank-${k.toLowerCase()}`} className="break-all font-mono text-base text-[#f4f7f5]">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const Offer = ({ info, onPaypal, onBank, busy }) => (
  <div data-testid="enrollment-offer">
    <p className="eyebrow mb-4">Training online · 90 giorni · 5 posti</p>
    <h1 className="font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">Ciao {info.name},<br /><span className="text-[#c4dcca]">il tuo posto è riservato.</span></h1>
    <p className="mt-6 max-w-xl text-base text-[#a3b1aa]">Per confermare l'iscrizione al percorso completa il pagamento della quota di partecipazione.</p>
    <div className="mt-10 grid gap-4 md:grid-cols-[1.2fr_1fr]">
      <ul className="space-y-3 border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-7" data-testid="enrollment-included">
        {INCLUDED.map((i) => <li key={i} className="flex items-start gap-3 text-sm text-[#f4f7f5]"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#98bfa2]" /> {i}</li>)}
      </ul>
      <div className="flex flex-col justify-between bg-[#98bfa2] p-7 text-[#050807]">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest">Quota per partecipante</p>
          <p data-testid="enrollment-price" className="mt-3 font-display text-7xl font-900 leading-none">{info.price} €</p>
        </div>
        <div className="mt-8 grid gap-2">
          <button type="button" data-testid="enrollment-paypal-btn" onClick={onPaypal} disabled={!!busy}
            className="flex items-center justify-center gap-2 bg-[#050807] px-6 py-4 font-display text-lg font-800 uppercase text-[#c4dcca] transition-colors hover:bg-[#111a15] disabled:opacity-60">
            {busy === "paypal" ? <Loader2 size={18} className="animate-spin" /> : <Wallet size={16} />} Paga con PayPal
          </button>
          <button type="button" data-testid="enrollment-bank-btn" onClick={onBank} disabled={!!busy}
            className="flex items-center justify-center gap-2 border-2 border-[#050807] px-6 py-4 font-display text-lg font-800 uppercase text-[#050807] transition-colors hover:bg-[#050807]/10 disabled:opacity-60">
            {busy === "bank" ? <Loader2 size={18} className="animate-spin" /> : <Landmark size={16} />} Paga con bonifico
          </button>
        </div>
        <p className="mt-3 text-xs text-[#050807]/75">Con PayPal puoi pagare anche con carta.</p>
      </div>
    </div>
    <Guarantee testid="enrollment-guarantee" className="mt-4" />
    <PaymentMethods testid="enrollment-payment-methods" className="mt-4" />
  </div>
);

export default function CoachingEnrollment() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("paypal") ? params.get("token") : null;
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(null);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/coaching/enrollment/${token}`);
        setInfo(data);
        if (data?.paid) return setState("paid");
        if (!orderId) return setState("offer");
        setState("checking");
        const { data: st } = await api.post(`/coaching/enrollment/${token}/paypal/capture`, { order_id: orderId });
        setState(st?.paid ? "paid" : "offer");
        if (!st?.paid) toast.error("Il pagamento PayPal non risulta completato");
      } catch (e) {
        if (e?.response?.status === 404 && !orderId) return setState("invalid");
        toast.error(formatError(e));
        setState(orderId ? "offer" : "invalid");
      }
    };
    load();
  }, [token, orderId]);

  const payPaypal = async () => {
    setBusy("paypal");
    try {
      const { data } = await api.post(`/coaching/enrollment/${token}/paypal`, { origin_url: window.location.origin });
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(formatError(e));
      setBusy(null);
    }
  };

  const payBank = async () => {
    setBusy("bank");
    try {
      const { data } = await api.post(`/coaching/enrollment/${token}/bonifico`);
      setBank(data);
      setState("bank");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <main data-testid="coaching-enrollment-page" className="min-h-screen">
      <Navbar />
      <CartDrawer />
      <div className="mx-auto max-w-5xl px-5 pb-24 pt-28 md:pt-36">
        {(state === "loading" || state === "checking") && (
          <p data-testid="enrollment-loading" className="flex items-center gap-3 font-mono text-sm text-[#a3b1aa]"><Loader2 size={16} className="animate-spin" /> {state === "checking" ? "Verifico il pagamento…" : "Caricamento…"}</p>
        )}
        {state === "invalid" && <p data-testid="enrollment-invalid" className="text-[#a3b1aa]">Questo link non è valido. Scrivi a hybridhealthlab@gmail.com per ricevere un nuovo link.</p>}
        {state === "offer" && info && <Offer info={info} onPaypal={payPaypal} onBank={payBank} busy={busy} />}
        {state === "bank" && bank && <BankDetails bank={bank} />}
        {state === "paid" && <Confirmed name={info?.name ?? ""} />}
      </div>
      <Footer />
    </main>
  );
}
